// The issue search box's pure parts: highlighted excerpts, inserting a
// suggestion at the caret, and the underline spans for warnings. The grammar
// itself lives on the server (src/projects/search-query.ts) — the client never
// parses a query, it only asks /search/suggest what fits at the caret.

/** What the server wraps a match in (HIGHLIGHT_START/END in search.ts). */
const MARK = /<mark>([\s\S]*?)<\/mark>/g

export type Segment = { text: string; mark: boolean }

/** A ts_headline excerpt as text runs. Rendered as text nodes, so whatever
 *  else the description contains is shown, never interpreted as markup. */
export function splitHighlights(snippet: string): Segment[] {
  const segments: Segment[] = []
  let at = 0
  for (const match of snippet.matchAll(MARK)) {
    if (match.index > at) segments.push({ text: snippet.slice(at, match.index), mark: false })
    if (match[1]) segments.push({ text: match[1], mark: true })
    at = match.index + match[0].length
  }
  if (at < snippet.length) segments.push({ text: snippet.slice(at), mark: false })
  return segments
}

/** Replaces [from, to) with a suggestion and says where the caret goes. A
 *  value gets a space after it so the next word can be typed straight away; a
 *  field name already ends in one. */
export function applySuggestion(query: string, from: number, to: number, insert: string) {
  const after = query.slice(to)
  const needsSpace = !insert.endsWith(" ") && !/^[\s,]/.test(after)
  const text = insert + (needsSpace ? " " : "")
  const value = query.slice(0, from) + text + after
  // Past the space that was already there, ready for the next word.
  const skip = !needsSpace && !insert.endsWith(" ") && after.startsWith(" ") ? 1 : 0
  return { value, caret: from + text.length + skip }
}

export type Warning = { message: string; start: number; end: number }

/** The query cut into plain and underlined runs, for the layer drawn under
 *  the input. Overlapping warnings merge into one run. */
export function warningSegments(query: string, warnings: Warning[]): Segment[] {
  const flagged = new Array<boolean>(query.length).fill(false)
  for (const w of warnings) {
    for (let i = Math.max(0, w.start); i < Math.min(query.length, w.end); i++) flagged[i] = true
  }
  const segments: Segment[] = []
  for (let i = 0; i < query.length; i++) {
    const last = segments.at(-1)
    if (last && last.mark === flagged[i]) last.text += query[i]
    else segments.push({ text: query[i]!, mark: flagged[i]! })
  }
  return segments
}

export type SortKey = "relevance" | "updated" | "created" | "priority" | "status" | "key" | "due"

/** "Sorted by relevance", "Sorted by updated (oldest first)". */
export function describeSort(sort: { key: SortKey; dir: "asc" | "desc" }[]) {
  if (!sort.length) return ""
  const words: Record<SortKey, [string, string]> = {
    relevance: ["least relevant first", "best match first"],
    updated: ["oldest change first", "latest change first"],
    created: ["oldest first", "newest first"],
    priority: ["lowest first", "highest first"],
    status: ["board order", "reverse board order"],
    key: ["lowest key first", "highest key first"],
    due: ["soonest first", "latest first"],
  }
  return sort
    .map((s) => {
      const [asc, desc] = words[s.key]
      return `${s.key} (${s.dir === "asc" ? asc : desc})`
    })
    .join(", ")
}
